import './ContactFormModal.css';

import LoadingButton from 'components/LoadingButton/LoadingButton';
import { useState } from 'react';
import { Card, Collapse, Form, Modal, Row, Col } from 'react-bootstrap';
import Pill from 'components/Pill/Pill';
import { useEffect } from 'react';
import SearchResultsList from 'components/SearchResultsList/SearchResultsList';
import {
    GET_ACCOUNTS_FILTER_BY_NAME_AND_ADDRESS,
    GET_CONTACT_BY_EMAIL_FOR_ACCOUNT,
    CREATE_CONTACT,
    UPDATE_CONTACT,
    useLazyQuery,
    useMutation,
} from 'utils/graphql';
import { ReactComponent as ExpandMoreIcon } from 'assets/icons/expand_more_icon_24.svg';
import { ReactComponent as ExpandLessIcon } from 'assets/icons/expand_less_icon_24.svg';
import { formatUTCDateTime } from 'utils/date.util';
import { capFirst } from 'utils/text.util';
import { isEmailValid, isPhoneNumberValid } from 'utils/validation.util';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';

const ContactFormModal = ({
    contactToEdit = null,
    account,
    isOpen,
    setIsOpen,
    onSuccess,
    blockedEmails,
    isB2B = true,
    property,
}) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [language, setLanguage] = useState(property?.language || 'en');
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [originalAccount, setOriginalAccount] = useState(null);
    const [commExpanded, setCommExpanded] = useState(true);
    const [commOptins, setCommOptins] = useState({ email: true, other: true });
    const [accountSearchQuery, setAccountSearchQuery] = useState('');
    const [showAccountResults, setShowAccountResults] = useState(false);
    const [accountSearchResults, setAccountSearchResults] = useState([]);
    const [formErrors, setFormErrors] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        contact: '',
    });
    const [validated, setValidated] = useState(false);

    const isEditing = contactToEdit != null;

    const languageOptions = [
        { value: 'en', text: 'english' },
        { value: 'fr', text: 'french' },
    ];

    const [getAccounts, { loading: isLoadingAccounts }] = useLazyQuery(
        GET_ACCOUNTS_FILTER_BY_NAME_AND_ADDRESS,
        { fetchPolicy: 'network-only' }
    );

    const [getContactByEmailForAccount] = useLazyQuery(GET_CONTACT_BY_EMAIL_FOR_ACCOUNT, {
        fetchPolicy: 'network-only',
    });

    const [createContactMutation] = useMutation(CREATE_CONTACT, {
        variables: {
            sfid: uuidv4(),
            optin_source: 'Retail Connection Portal',
            optin_type: 'Tacit',
        },
    });

    const [updateContactMutation] = useMutation(UPDATE_CONTACT, {
        variables: {
            optin_source: 'Retail Connection Portal',
            optin_type: 'Tacit',
        },
    });

    const resetForm = () => {
        setIsLoading(false);
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhone('');
        setLanguage(property?.language || 'en');
        setCommExpanded(true);
        setCommOptins(
            property?.language == 'fr'
                ? { email: false, other: false }
                : { email: true, other: true }
        );
        setAccountSearchQuery('');
        setShowAccountResults(false);
        setAccountSearchResults([]);
        setFormErrors({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            company: '',
            contact: '',
        });
        setValidated(false);
    };

    const searchAccounts = () => {
        getAccounts({
            variables: {
                like: `%${accountSearchQuery}%`,
                not_like: 'ivanhoe%',
            },
            onCompleted: (data) => {
                setAccountSearchResults(data.table.records.map((record) => record.account));
            },
        });
    };

    const validate = async () => {
        let _formErrors = {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            company: '',
            contact: '',
        };
        let errors = false;
        if (!firstName) {
            _formErrors.firstName = 'first_name_empty_error';
            errors = true;
        }
        if (!lastName) {
            _formErrors.lastName = 'last_name_empty_error';
            errors = true;
        }
        if (!email) {
            _formErrors.email = 'email_empty_error';
            errors = true;
        } else if (!isEmailValid(email)) {
            _formErrors.email = 'valid_email_error';
            errors = true;
        } else if (
            blockedEmails.filter((b) => {
                return b.email === email;
            }).length > 0
        ) {
            _formErrors.email = 'email_purchases_prohibited';
            errors = true;
        }

        if (phone && !isPhoneNumberValid(phone)) {
            _formErrors.phone = 'phone_length_error';
            errors = true;
        }

        if (!selectedAccount) {
            _formErrors.company = 'company_empty_error';
            errors = true;
        }

        setFormErrors(_formErrors);
        if (errors) {
            setValidated(true);
            return false;
        } else if (!isEditing || contactToEdit.email !== email) {
            const { data } = await getContactByEmailForAccount({
                variables: {
                    accountid: selectedAccount.sfid,
                    email,
                },
            });
            if (data.table.count > 0) {
                setFormErrors({
                    ..._formErrors,
                    contact: 'contact_email_exists_error',
                });
                setValidated(true);
                return false;
            }
            return true;
        }
        return true;
    };

    const createContact = async () => {
        setIsLoading(true);
        if (await validate()) {
            const _firstName = capFirst(firstName);
            const _lastName = capFirst(lastName);
            const now = new Date();
            let variables = {
                name: `${_firstName} ${_lastName}`,
                firstname: _firstName,
                lastname: _lastName,
                email,
                phone,
                accountid: selectedAccount.sfid,
                optin_email: commOptins.email,
                optin_date: commOptins.email ? formatUTCDateTime(now) : null,
                optin_other: commOptins.other,
                createddate: formatUTCDateTime(now),
                language,
                ott_customer: false,
            };
            createContactMutation({
                variables,
            })
                .then((response) => {
                    if (response.errors) {
                        setFormErrors({
                            ...formErrors,
                            contact: 'contact_creation_fail',
                        });
                        setIsLoading(false);
                    } else {
                        setIsOpen(false);
                        onSuccess(response.data.createRow.contact);
                    }
                })
                .catch((error) => {
                    console.log('error', error);
                    setFormErrors({
                        ...formErrors,
                        contact: 'contact_creation_fail',
                    });
                    setIsLoading(false);
                });
        } else {
            setIsLoading(false);
        }
    };

    const editContact = async () => {
        setIsLoading(true);
        if (await validate()) {
            const _firstName = capFirst(firstName);
            const _lastName = capFirst(lastName);
            const now = new Date();
            let variables = {
                id: contactToEdit.id,
                name: `${_firstName} ${_lastName}`,
                firstname: _firstName,
                lastname: _lastName,
                email,
                phone,
                accountid: selectedAccount.sfid,
                optin_email: commOptins.email,
                optin_date:
                    contactToEdit.optin_email !== commOptins.email
                        ? formatUTCDateTime(now)
                        : contactToEdit.optin_date,
                optin_other: commOptins.other,
                language,
                ott_customer: contactToEdit.ott_customer,
                hvc__c: contactToEdit.hvc__c,
                hvc_last_modified_by__c: contactToEdit.hvc_last_modified_by__c,
            };

            updateContactMutation({
                variables,
            })
                .then((response) => {
                    if (response.errors) {
                        setFormErrors({
                            ...formErrors,
                            contact: 'contact_update_fail',
                        });
                        setIsLoading(false);
                    } else {
                        setIsOpen(false);
                        const newAccount =
                            selectedAccount.id !== originalAccount.id ? selectedAccount : null;
                        onSuccess(
                            { ...response.data.updateRow.contact, sfid: contactToEdit.sfid },
                            newAccount
                        );
                    }
                })
                .catch((error) => {
                    console.log('error', error);
                    setFormErrors({
                        ...formErrors,
                        contact: 'contact_update_fail',
                    });
                    setIsLoading(false);
                });
        } else {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => searchAccounts(), 500);
        return () => {
            clearTimeout(timer);
        };
    }, [accountSearchQuery]);

    useEffect(() => {
        setSelectedAccount(account);
    }, [account]);

    useEffect(() => {
        if (validated) setValidated(false);
    }, [firstName, lastName, email, phone, selectedAccount]);

    useEffect(() => {
        if (!isOpen) return;
        resetForm();
        if (isEditing) {
            setOriginalAccount(account);
            setFirstName(contactToEdit.firstname ? contactToEdit.firstname : '');
            setLastName(contactToEdit.lastname ? contactToEdit.lastname : '');
            setEmail(contactToEdit.email ? contactToEdit.email : '');
            setPhone(contactToEdit.phone ? contactToEdit.phone : '');
            setLanguage(contactToEdit.language ? contactToEdit.language : 'en');
            setCommOptins({ email: contactToEdit.optin_email, other: contactToEdit.optin_other });
        }
    }, [isOpen]);

    useEffect(() => {
        if (property?.language === 'fr') {
            setCommOptins({
                email: false,
                other: false,
            });
        }
    }, [property]);

    return (
        <Modal show={isOpen} onHide={() => setIsOpen(false)} className="contact-form-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    <h5>{t(isEditing ? 'edit_contact' : 'create_contact')}</h5>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <span className="required-field">*</span> {t('first_name')}
                        </Form.Label>
                        <Form.Control
                            isInvalid={validated && formErrors.firstName}
                            type="text"
                            value={firstName}
                            onChange={(event) => setFirstName(event.target.value)}
                        />
                        {formErrors.firstName && (
                            <Form.Control.Feedback type="invalid">
                                {t(formErrors.firstName)}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <span className="required-field">*</span> {t('last_name')}
                        </Form.Label>
                        <Form.Control
                            isInvalid={validated && formErrors.lastName}
                            type="text"
                            value={lastName}
                            onChange={(event) => setLastName(event.target.value)}
                        />
                        {formErrors.lastName && (
                            <Form.Control.Feedback type="invalid">
                                {t(formErrors.lastName)}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <span className="required-field">*</span> {t('email')}
                        </Form.Label>
                        <Form.Control
                            isInvalid={validated && formErrors.email}
                            type="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                        />
                        {formErrors.email && (
                            <Form.Control.Feedback type="invalid">
                                {t(formErrors.email)}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('phone')}</Form.Label>
                        <Form.Control
                            isInvalid={validated && formErrors.phone}
                            type="text"
                            value={phone}
                            onChange={(event) => setPhone(event.target.value)}
                        />
                        {formErrors.phone && (
                            <Form.Control.Feedback type="invalid">
                                {t(formErrors.phone)}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <span className="required-field">*</span> {t('language')}
                        </Form.Label>
                        <Form.Select
                            value={language}
                            onChange={(event) => setLanguage(event.target.value)}
                        >
                            {languageOptions.map((option, i) => (
                                <option value={option.value} key={i}>
                                    {t(option.text)}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <span className="required-field">*</span> {t('company_name')}
                        </Form.Label>
                        {selectedAccount ? (
                            <Pill
                                isLocked={!isEditing || !isB2B}
                                closeAction={() => {
                                    setSelectedAccount(null);
                                    searchAccounts();
                                }}
                            >
                                {isB2B ? selectedAccount.name : 'B2C'}
                            </Pill>
                        ) : (
                            <div className="position-relative">
                                <Form.Control
                                    type="text"
                                    isInvalid={validated && formErrors.company}
                                    value={accountSearchQuery}
                                    onChange={(e) => setAccountSearchQuery(e.target.value)}
                                    onClick={() =>
                                        !showAccountResults && setShowAccountResults(true)
                                    }
                                />
                                {formErrors.company && (
                                    <Form.Control.Feedback type="invalid">
                                        {t(formErrors.company)}
                                    </Form.Control.Feedback>
                                )}
                                <SearchResultsList
                                    show={showAccountResults}
                                    onClickOutside={() => {
                                        setShowAccountResults(false);
                                    }}
                                    onClickCallback={(result) => {
                                        setSelectedAccount(result);
                                        setShowAccountResults(false);
                                    }}
                                    isLoadingResults={isLoadingAccounts}
                                    searchResults={accountSearchResults}
                                    getText={(result) => result.name}
                                    getSubText={(result) => result.address_line_1__c}
                                />
                            </div>
                        )}
                    </Form.Group>
                    <Card>
                        <Card.Header
                            className="d-flex flex-row justify-content-between"
                            role="button"
                            onClick={() => setCommExpanded(!commExpanded)}
                        >
                            <span>{t('communications')}</span>
                            {commExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </Card.Header>
                        <Collapse in={commExpanded}>
                            <Card.Body>
                                <Form.Label>{t('optin')}</Form.Label>
                                <div className="d-flex align-items-center justify-content-evenly">
                                    <Form.Check
                                        type="checkbox"
                                        label={t('email')}
                                        checked={commOptins.email}
                                        onChange={(e) =>
                                            setCommOptins({
                                                ...commOptins,
                                                email: e.target.checked,
                                            })
                                        }
                                    />
                                    <Form.Check
                                        type="checkbox"
                                        label={t('other')}
                                        checked={commOptins.other}
                                        onChange={(e) =>
                                            setCommOptins({
                                                ...commOptins,
                                                other: e.target.checked,
                                            })
                                        }
                                    />
                                </div>
                            </Card.Body>
                        </Collapse>
                    </Card>
                </Form>
                {validated && formErrors.contact && (
                    <div
                        className="invalid-feedback mt-3 fw-bold"
                        style={{ display: formErrors.contact ? 'block' : 'none' }}
                    >
                        {t(formErrors.contact)}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <LoadingButton
                    onClick={isEditing ? editContact : createContact}
                    isLoading={isLoading}
                >
                    {t(isEditing ? 'edit' : 'create')}
                </LoadingButton>
            </Modal.Footer>
        </Modal>
    );
};

export default ContactFormModal;
